import * as Sentry from '@sentry/vue'
import jwt_decode from 'jwt-decode'
import {useUserStore} from '@/store/user'

// Throttles how often the same error is reported, so retried/repeated failures don't flood Sentry
const SEND_THROTTLE_MS = 60000
const lastSentByFingerprint = new Map()

export default defineNuxtPlugin(() => {
  const router = useRouter()
  const app = useNuxtApp()
  const vueApp = app.vueApp
  const config = useRuntimeConfig()

  Sentry.init({
    app: vueApp,
    dsn: config.public.sentryDsn,
    environment: config.public.environment,
    release: process.env.SENTRY_RELEASE || 'DEV',
    tunnel: '/monitoring/envelope',
    sendClientReports: false,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications'
    ],
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router)
      })
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: config.public.sentryTracesSampleRate,
    beforeSend(event, hint) {
      const error = hint?.originalException
      if (error?.statusCode === 404 || error?.statusCode === '404') {
        return null
      }

      const fingerprint = error?.message || event.exception?.values?.[0]?.value || event.message || 'unknown'
      const now = Date.now()
      const lastSent = lastSentByFingerprint.get(fingerprint)
      if (lastSent && now - lastSent < SEND_THROTTLE_MS) {
        return null
      }
      lastSentByFingerprint.set(fingerprint, now)

      try {
        const userStore = useUserStore()
        if (userStore) {
          const {accessToken} = storeToRefs(userStore)
          if (accessToken?.value && accessToken.value !== '') {
            const decodedToken = jwt_decode(accessToken.value)
            const salesArea = decodedToken?.salesAreaName ?? ''
            const companyNo = decodedToken?.companyNo ?? ''
            const userUuid = decodedToken?.oid ?? ''
            const issuer = decodedToken?.iss ?? ''
            // Extract only the GUID from the URL
            const guidMatch = issuer.match(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i)
            const customerUuid = guidMatch ? guidMatch[0] : ''
            const customerKey = companyNo && salesArea ? `${companyNo}_${salesArea}` : null

            event.contexts = {
              ...event.contexts,
              'enext Application Data': {
                CustomerUuid: customerUuid,
                CustomerKey: customerKey,
                CompanyNo: companyNo,
                UserUuid: userUuid,
                System: 'enext',
                FstSource: 'Frontend'
              }
            }
          }
        }
      } catch (e) {
        // Silently fail if store is not available yet
      }
      return event
    }
  })

  app.provide('sentry', Sentry)
})
